import React, { useState } from "react";
import { ZButton, ZIcon, ZTypography } from "@atoms";
import { EIcons } from "@atoms/ZIcon/interface";
import { ZTable } from "@organisms";
import { ColumnType } from "antd/lib/table";
import { ZImportProductsToQuoteDrawerPreviewProps } from "./interface";
import { EColors } from "@atoms/ZColors/interface";
import {
	QuoteProduct,
	QuoteBlockData
} from "@/Contracts/ProposalBuilder/Block";

import { currencyFormatter } from "@/Utils";
import ZImportFooter from "../ZImportFooter";
import { EFooterVariant } from "../ZImportFooter/interface";
import ZInput from "@molecules/ZInput";
import "./index.less";

const ZImportProductsToQuoteDrawerPreview: React.FC<
	ZImportProductsToQuoteDrawerPreviewProps
> = ({
	data,
	onImportConfirmed,
	onClose,
	isLoading = false,
	isAiImport = true
}) => {
	const [sectionName, setSectionName] = useState("");
	const [showErrorLogs, setShowErrorLogs] = useState(false);

	/* Extract Products from LAST SECTION ONLY (contains PDF products) */
	let displayData: QuoteProduct[] = [];

	if (data?.updated_block?.data && "sections" in data.updated_block.data) {
		const quoteBlockData = data.updated_block.data as QuoteBlockData;

		// Get ONLY the last section (PDF products are always placed in the last section by backend)
		const lastSection =
			quoteBlockData.sections[quoteBlockData.sections.length - 1];

		if (lastSection?.products) {
			displayData = lastSection.products;
		}
	}

	const recordsAdded = displayData.length;
	const recordsSkipped = data?.rowsSkippedCount || 0;
	const currency = data?.currency || "USD";

	// Helper function to format billing period
	const formatBillingPeriod = (period: string | null | undefined): string => {
		if (!period) return "";
		const periodMap: Record<string, string> = {
			one_time: "One-time",
			monthly: "Monthly",
			annual: "Annual",
			quarterly: "Quarterly",
			semi_annually: "Semi-Annually",
			weekly: "Weekly",
			daily: "Daily",
			hourly: "Hourly"
		};
		return periodMap[period] || period;
	};

	const columns: ColumnType<QuoteProduct>[] = [
		{
			title: "Product Name",
			dataIndex: "product_name",
			key: "product_name",
			width: 200,
			render: (_, r) => r.name || ""
		},
		{
			title: "Product Type",
			dataIndex: "product_type",
			key: "product_type",
			width: 150,
			render: (_, r) => r.product_type || ""
		},
		{
			title: "Product Description",
			dataIndex: "product_description",
			key: "product_description",
			width: 250,
			render: (_, r) => r.description || ""
		},
		{
			title: "Category",
			dataIndex: "category",
			key: "category",
			width: 150,
			render: (_, r) => r.product_category || ""
		},
		{
			title: "Manufacturer Part Number",
			dataIndex: "manufacturer_part_number",
			key: "manufacturer_part_number",
			width: 200,
			render: (_, r) => r.manufacturer_part_no || ""
		},
		{
			title: "Manufacturer",
			dataIndex: "manufacturer",
			key: "manufacturer",
			width: 150,
			render: (_, r) => r.manufacturer || ""
		},
		{
			title: "Vendor",
			dataIndex: "vendor",
			key: "vendor",
			width: 150,
			render: (_, r) => r.vendor_name || ""
		},
		{
			title: "Vendor SKU",
			dataIndex: "vendor_sku",
			key: "vendor_sku",
			width: 150,
			render: (_, r) => r.vendor_sku_number || ""
		},
		{
			title: "Warehouse",
			dataIndex: "warehouse",
			key: "warehouse",
			width: 150,
			render: (_, r) => r.selected_warehouse || ""
		},
		{
			title: "Product selected?",
			dataIndex: "product_selected",
			key: "product_selected",
			width: 150,
			render: () => "Yes"
		},
		{
			title: "Is child of a Bundle Product?",
			dataIndex: "is_child_bundle",
			key: "is_child_bundle",
			width: 220,
			render: (_, r) =>
				r.bundled_products && r.bundled_products.length > 0
					? "Yes"
					: "No"
		},
		{
			title: "Bundle Product Id",
			dataIndex: "bundle_product_id",
			key: "bundle_product_id",
			width: 150,
			render: (_, r) => (r as any).bundle_product_id || ""
		},
		{
			title: "Bundle Product Name",
			dataIndex: "bundle_product_name",
			key: "bundle_product_name",
			width: 200,
			render: (_, r) => (r as any).bundle_product_name || ""
		},
		{
			title: "Billing Period",
			dataIndex: "billing_period",
			key: "billing_period",
			width: 150,
			render: (_, r) => formatBillingPeriod(r.selected_billing_period)
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			width: 100,
			align: "right",
			render: (_, r) => r.quantity || ""
		},
		{
			title: "Cost Price per unit",
			dataIndex: "cost_price_per_unit",
			key: "cost_price_per_unit",
			width: 150,
			align: "right",
			render: (_, r) =>
				currencyFormatter(r.selected_cost_price, "", undefined, {
					currency,
					locale: "en-US"
				})
		},
		{
			title: "Sell Price per unit",
			dataIndex: "sell_price_per_unit",
			key: "sell_price_per_unit",
			width: 150,
			align: "right",
			render: (_, r) =>
				currencyFormatter(r.sell_price, "", undefined, {
					currency,
					locale: "en-US"
				})
		},
		{
			title: "Margin",
			dataIndex: "margin",
			key: "margin",
			width: 180,
			align: "right",
			render: (_, r) => {
				const marginValue = r.item_margin_value || 0;
				const marginPercent = r.item_margin_percent || 0;
				return `${currencyFormatter(marginValue, "", undefined, {
					currency,
					locale: "en-US"
				})} (${marginPercent}%)`;
			}
		},
		{
			title: "Discount on unit sell price",
			dataIndex: "discount_on_unit_sell_price",
			key: "discount_on_unit_sell_price",
			width: 200,
			align: "right",
			render: (_, r) => {
				const discountValue = r.discount || 0;
				const discountPercent = r.discount_percentage || 0;
				return `${currencyFormatter(discountValue, "", undefined, {
					currency,
					locale: "en-US"
				})} (${discountPercent}%)`;
			}
		},
		{
			title: "Set Up Price",
			dataIndex: "set_up_price",
			key: "set_up_price",
			width: 150,
			align: "right",
			render: (_, r) =>
				currencyFormatter(r.selected_setup_price, "", undefined, {
					currency,
					locale: "en-US"
				})
		},
		{
			title: "Discount on Set Up Price",
			dataIndex: "discount_on_set_up_price",
			key: "discount_on_set_up_price",
			width: 200,
			align: "right",
			render: (_, r) => {
				const setupDiscountValue = r.selected_setup_discount || 0;
				const setupDiscountPercent = r.setup_discount_percentage || 0;
				return `${currencyFormatter(setupDiscountValue, "", undefined, {
					currency,
					locale: "en-US"
				})} (${setupDiscountPercent}%)`;
			}
		},
		{
			title: "Tax Amount",
			dataIndex: "tax_amount",
			key: "tax_amount",
			width: 150,
			align: "right",
			render: () => ""
		},
		{
			title: "Sell price (considering tax and discount)",
			dataIndex: "sell_price_with_tax_discount",
			key: "sell_price_with_tax_discount",
			width: 250,
			align: "right",
			render: (_, r) => {
				const finalPrice = (r.sell_price || 0) - (r.discount || 0);
				return currencyFormatter(finalPrice, "", undefined, {
					currency,
					locale: "en-US"
				});
			}
		},
		{
			title: "Setup price (considering tax and discount)",
			dataIndex: "setup_price_with_tax_discount",
			key: "setup_price_with_tax_discount",
			width: 250,
			align: "right",
			render: (_, r) => {
				const finalSetupPrice =
					(r.selected_setup_price || 0) -
					(r.selected_setup_discount || 0);
				return currencyFormatter(finalSetupPrice, "", undefined, {
					currency,
					locale: "en-US"
				});
			}
		}
	];

	const aiSummary =
		data?.data_details ||
		(displayData.length > 0
			? `Successfully processed your ${
					data?.document_type || "document"
			  } from ${data?.vendor_name || "vendor"}. The document shows ${
					displayData.length
			  } product line${
					displayData.length !== 1 ? "s" : ""
			  } imported with their specifications and pricing intact.`
			: "No products were found in the uploaded document. Please check the file format and try again.");

	// ERROR LOGS VIEW

	if (showErrorLogs) {
		return (
			<div className="h-full flex flex-col">
				<div className="preview-gradient-header">
					<ZTypography.ZTitle
						level={3}
						color={EColors.GRAY}
						colorVariant={9}
						className="preview-section-title"
					>
						Error logs
					</ZTypography.ZTitle>

					<div className="rounded border border-gray-3 px-4 py-2 min-h-9 flex items-center gap-6 text-gray-7">
						{displayData.length > 0 ? (
							<div className="flex justify-between flex-wrap gap-3 w-full">
								<div className="flex items-center gap-2 shrink-0">
									<ZIcon
										name={EIcons.CHECKCIRCLE}
										size="md"
										color={EColors.GREEN}
										colorVariant={6}
									/>
									<ZTypography.ZText>
										{recordsSkipped > 0
											? `${recordsAdded} rows imported successfully`
											: "All rows imported successfully"}
									</ZTypography.ZText>
								</div>

								<div className="flex items-center gap-4 whitespace-nowrap">
									<ZTypography.ZText>
										Records Added: {recordsAdded}
									</ZTypography.ZText>
									<ZTypography.ZText>
										Records Skipped: {recordsSkipped}
									</ZTypography.ZText>
								</div>
							</div>
						) : (
							<div className="bg-yellow-1 border border-yellow-4 border-solid rounded flex items-center justify-center gap-2 text-sm text-yellow-9 py-2 w-full">
								<ZIcon
									name={EIcons.EXCLAMATIONCIRCLE}
									size="sm"
									color={EColors.GOLD}
									colorVariant={6}
								/>
								<ZTypography.ZText>
									No records found to import
								</ZTypography.ZText>
							</div>
						)}
					</div>
				</div>

				<div className="preview-content-container">
					<div className="bg-gold-1 border border-gold-3 border-solid rounded p-3">
						<div className="mb-3">
							<ZIcon
								name={EIcons.EXCLAMATIONCIRCLE}
								size="md"
								className="mr-2"
							/>
							<ZTypography.ZText className="font-semibold text-gray-8">
								Rows Ignored: {recordsSkipped}
							</ZTypography.ZText>
						</div>
						{data?.errors && data.errors.length > 0 ? (
							<div className="ml-8">
								{data.errors.map((error, index) => (
									<ZTypography.ZText
										key={index}
										className="text-gray-7 mb-2 block"
									>
										• {error.message}
									</ZTypography.ZText>
								))}
							</div>
						) : (
							<ZTypography.ZText className="text-gray-7 ml-8">
								{recordsSkipped > 0
									? `${recordsSkipped} rows were skipped due to missing or invalid data.`
									: "No errors to display."}
							</ZTypography.ZText>
						)}
					</div>
				</div>

				<div className="preview-footer-container mb-4">
					<div className="preview-footer-decoration left"></div>

					<ZButton
						onClick={() => setShowErrorLogs(false)}
						className=" border border-gray-5 cursor-pointer rounded-md text-sm hover:bg-gray-50 h-8 px-4 w-29"
					>
						Back
					</ZButton>

					<div className="preview-footer-decoration right"></div>
				</div>
			</div>
		);
	}

	// MAIN PREVIEW VIEW

	return (
		<div className="h-full flex flex-col">
			<div className="preview-gradient-header">
				{onClose && (
					<button
						onClick={onClose}
						className="preview-close-button"
						aria-label="Close"
					>
						<ZIcon name={EIcons.CLOSE} size="sm" />
					</button>
				)}

				<ZTypography.ZTitle level={3} className="preview-section-title">
					Preview
				</ZTypography.ZTitle>

				<div className="flex items-center gap-3">
					<ZTypography.ZText className="font-medium whitespace-nowrap">
						Section Name
					</ZTypography.ZText>

					<ZInput
						placeholder="Enter the section name on quote block"
						value={sectionName}
						onChange={e => setSectionName(e.target.value)}
						width="full"
					/>
				</div>
			</div>

			<div className="preview-content-container">
				<div className="preview-table-border">
					<ZTable<QuoteProduct>
						dataSource={displayData}
						columns={columns}
						rowKey={(record, index) => record.id || String(index)}
						loading={isLoading}
						pagination={false}
						locale={{
							emptyText:
								"No products found in the uploaded document"
						}}
						scroll={{ x: true, scrollToFirstRowOnChange: true }}
						isTopBorder={false}
						showHeader={true}
					/>
				</div>

				{displayData.length > 0 && (
					<div className="preview-status-badge">
						<div className="flex justify-between items-center w-full">
							<div className="flex items-center gap-2">
								<ZIcon
									name={EIcons.CHECKCIRCLE}
									size="sm"
									color={EColors.GREEN}
									colorVariant={6}
								/>
								<ZTypography.ZText className="text-gray-7 text-sm">
									{recordsSkipped > 0
										? `${recordsAdded} rows imported successfully`
										: "All rows imported successfully"}
								</ZTypography.ZText>
							</div>

							<div className="flex items-center gap-6 text-gray-7 text-sm whitespace-nowrap">
								<ZTypography.ZText>
									Records Added: {recordsAdded}
								</ZTypography.ZText>
								<ZTypography.ZText>
									Records Skipped: {recordsSkipped}
								</ZTypography.ZText>

								<a
									href="#"
									onClick={e => {
										e.preventDefault();
										setShowErrorLogs(true);
									}}
									className="text-blue-600 underline flex items-center gap-1"
								>
									View error logs
									<ZIcon name={EIcons.ARROWRIGHT} size="sm" />
								</a>
							</div>
						</div>
					</div>
				)}

				{isAiImport && (
					<>
						<div className="preview-ai-summary-container">
							<div className="flex items-center mb-2">
								<ZTypography.ZTitle
									level={4}
									className="font-semibold text-gray-8 m-0"
								>
									<ZIcon
										name={EIcons.AIPARSING}
										size="md"
										color={EColors.BLUE}
										colorVariant={6}
									/>
									AI Summary
								</ZTypography.ZTitle>
							</div>

							<ZTypography.ZText className="m-0">
								{aiSummary}
							</ZTypography.ZText>
						</div>
					</>
				)}
			</div>

			<ZImportFooter
				variant={EFooterVariant.IMPORT_ZBUTTON}
				buttonText="Add to Quote"
				onClick={() => onImportConfirmed?.(sectionName)}
				disabled={displayData.length === 0}
				showAiDisclaimer={isAiImport}
				iconName={EIcons.STARICON}
				iconProps={{ colorVariant: 1 }}
				className="preview-import-button"
			/>
		</div>
	);
};

export default ZImportProductsToQuoteDrawerPreview;
